import { ComparisonModeStrings } from 'enums/ComparisonMode'
import CrosstabSortOption from 'enums/CrosstabSortOption'
import CrosstabDisplayTypeDetail from 'enums/CrosstabDisplayTypeDetail'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { Point } from 'components/visualization/Heatmap/types'
import i18n from 'i18n'
import { PivotWithCellMarker, CrosstabWidgetsType, SeriesData } from '../../types'
import { arrangeResult, genSeriesData, genTotal, sortByMetric } from '../utils/displayDataUtils'

export const getCrossTabSetting = (
  result: PivotWithCellMarker[],
  setting: CrosstabWidgetsType,
  rowIds: string[],
  rowNames: string[],
  colIds: string[],
  colNames: string[],
  rowComparisonMode: ComparisonModeStrings,
  colComparisonMode: ComparisonModeStrings
) => {
  const heatmapRowIds = [...rowIds]
  const heatmapRowNames = [...rowNames]

  const heatmapColIds = [...colIds]
  const heatmapColNames = [...colNames]

  const arrangedResult = arrangeResult(
    result,
    heatmapRowIds,
    heatmapColIds,
    rowComparisonMode,
    colComparisonMode
  )

  // gen series data
  const seriesData = genSeriesData(arrangedResult, setting)
  switch (setting.ctSort) {
    case CrosstabSortOption.ROW_METRIC_ASC:
    case CrosstabSortOption.ROW_METRIC_DES:
    case CrosstabSortOption.COLUMN_METRIC_ASC:
    case CrosstabSortOption.COLUMN_METRIC_DES:
      sortByMetric(
        seriesData,
        setting,
        heatmapRowIds,
        heatmapRowNames,
        heatmapColIds,
        heatmapColNames
      )
      break
    default:
      if (setting.ctPercentShare !== CrosstabDisplayTypeDetail.ROW_GRAND_TOTAL) {
        // insert total cell
        genTotal(seriesData, setting.ctDisplay!, setting.ctDisplayTotal!, setting.viewOption!, true)
      }
  }

  return {
    heatmapSeriesData: seriesData,
    heatmapRowIds,
    heatmapRowNames,
    heatmapColIds,
    heatmapColNames
  }
}

const compactFormat = Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
  // @ts-ignore
  // Don't know why Safari(15.6.1+) doesn't honor maximumFractionDigits.
  // Therefore, the experimental roundingPriority is added to force Safari to use the result calculated from maximumFractionDigits.
  roundingPriority: 'lessPrecision'
})

export const valueFormatter = ({ value, isTooltip }: Point) => {
  const thousandFormat = Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  })
  if (isTooltip) {
    return thousandFormat.format(value)
  }
  return value > 100000 ? compactFormat.format(value) : thousandFormat.format(value)
}

export const valueFormatterRoundOneDecimal = ({ value, isTooltip }: Point) => {
  const thousandFormat = Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1
  })
  if (isTooltip) {
    return thousandFormat.format(value)
  }
  return value > 100000 ? compactFormat.format(value) : thousandFormat.format(value)
}

export const percentFormatter = (point: Point) => {
  if (point.isColorAxis === true) {
    return `${Math.round(point.value)}%`
  }
  if (point.value > 0 && point.value < 1) {
    return '<1%'
  }
  return `${Math.round(point.value)}%`
}

export const hybridFormatter = (point: Point) => {
  if (point.isAggregation) {
    return valueFormatter(point)
  }
  return percentFormatter(point)
}

export const getValueFormatter = (
  viewOption: InsightViewOptionStrings,
  displayValue: boolean,
  displayRowGrandTotal: boolean
) => {
  if ([InsightViewOption.STAR_RATINGS, InsightViewOption.SURVEY_SCORES].includes(viewOption)) {
    return valueFormatterRoundOneDecimal
  }
  if (displayValue) {
    return valueFormatter
  }
  if (displayRowGrandTotal) {
    return hybridFormatter
  }
  return percentFormatter
}

export const getColorSetting = (viewOption: InsightViewOptionStrings) => {
  switch (viewOption) {
    case InsightViewOption.MENTIONS:
      return 'gradient.mention'
    case InsightViewOption.POSTS:
      return 'gradient.post'
    case InsightViewOption.NET_SENTIMENT:
      return 'gradient.netSentiment'
    case InsightViewOption.IMPRESSIONS:
      return 'gradient.potentialImpressions'
    case InsightViewOption.PASSION_INTENSITY:
      return 'gradient.passionIntensity'
    case InsightViewOption.UNIQUE_AUTHORS:
      return 'gradient.authors'
    case InsightViewOption.ENGAGEMENTS:
      return 'gradient.totalEngagement'
    case InsightViewOption.ENGAGEMENT_RATIO_ONLY:
      return 'gradient.engagementPerPost'
    case InsightViewOption.STAR_RATINGS:
      return 'gradient.overallStarRatings'
    case InsightViewOption.SURVEY_SCORES:
      return 'gradient.overallSurveyScores'
    case InsightViewOption.COMMENTS:
      return 'gradient.comments'
    case InsightViewOption.LIKES:
      return 'gradient.likes'
    case InsightViewOption.SHARES:
      return 'gradient.shares'
    case InsightViewOption.SCORE:
      return 'gradient.score'
    case InsightViewOption.POSITIVE:
      return 'gradient.positives'
    case InsightViewOption.NEGATIVE:
      return 'gradient.negatives'
    default:
      return ''
  }
}

export const getRange = (displayShare: boolean, viewOption: InsightViewOptionStrings) => {
  if (displayShare) {
    return [0, 100]
  }
  switch (viewOption) {
    case InsightViewOption.NET_SENTIMENT:
      return [-100, 100]
    case InsightViewOption.STAR_RATINGS:
      return [1, 5]
    case InsightViewOption.SURVEY_SCORES:
      return [0, 10]
    default:
      return [undefined, undefined]
  }
}

export const getDisabledCellToolTip = (seriesData: SeriesData) => {
  if (seriesData.disable === true) {
    return i18n.t('dataDisable')
  }
  if (seriesData.noCount === true) {
    return i18n.t('dataNoCount')
  }
  if (seriesData.bsNotSupported === true) {
    return i18n.t('dataLanguageNotSupport')
  }
  return ''
}
